import React, { useEffect, useRef } from "react";
import {
  Box,
  Divider,
  Grid,
  FormControl,
  FormControlLabel,
  Typography,
  Radio,
  RadioGroup
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";

import HaklaiSectionAccordion from "../shared/ui/HaklaiSectionAccordion";
import LabeledTextField from "../shared/ui/HaklaiLabeledTextField";
import HelpDialog from "../../../layout/HelpDialog";
import haklaiHelpTexts from "../shared/haklaiHelpTexts";
import { haklaiLockableSectionStyles } from "../shared/table/haklaiTableShared";
import { HAKLAI_ANOTHER_QUESTIONS_LABELS } from "./haklaiAnotherQuestionsTexts";

// This section only needs the greyed/desaturated locked-content treatment.
// The notice cascade in `HaklaiCalcView.js` keeps lock banners on earlier
// sections, so there is no banner slot here.
const useStyles = makeStyles(() => ({
  lockedSectionContent: haklaiLockableSectionStyles.lockedSectionContent
}));
const FIELD_ERROR_STYLE = { minHeight: 18, display: "block" };

/**
 * Owns the Haklai additional-questions section.
 *
 * Responsibilities:
 * - collects offset and afkaa inputs that feed the final calculation
 * - renders only input fields; result effects are displayed in results views
 * - keeps the field names aligned with the deal payload
 */
const HaklaiAnotherQuestionsSection = ({
  kizuzIski,
  kizuzHoni,
  afkaa,
  handleOnChange,
  expanded = undefined,
  onToggleExpanded = undefined,
  locked = false
}) => {
  const classes = useStyles();
  const contentRef = useRef(null);

  useEffect(() => {
    if (!locked || !contentRef.current) return;
    const activeElement = document.activeElement;
    if (activeElement && contentRef.current.contains(activeElement)) {
      activeElement.blur();
    }
  }, [locked]);

  return (
    <HaklaiSectionAccordion
      summaryText={HAKLAI_ANOTHER_QUESTIONS_LABELS.sectionTitle}
      defaultExpanded={false}
      expanded={expanded}
      onToggleExpanded={onToggleExpanded}
    >
      <Box
        ref={contentRef}
        className={[
          "haklai-another-questions",
          locked ? classes.lockedSectionContent : ""
        ].filter(Boolean).join(" ")}
        // Empty-string dance is a React 17 workaround — passing a
        // boolean would render literal "true"/"false" into the DOM.
        // On React 18+ this simplifies to `inert={locked}`.
        inert={locked ? "" : undefined}
      >
        <Grid container direction="column" spacing={2}>
        <Grid container item direction="row" spacing={10} alignItems="center">
          <Grid item>
            <LabeledTextField
              type="number"
              value={kizuzIski}
              textFieldWidth={185}
              name="kizuzIski"
              label={HAKLAI_ANOTHER_QUESTIONS_LABELS.businessLossOffset}
              helpDialog={haklaiHelpTexts.kizuzIski}
              onChange={handleOnChange}
            />
          </Grid>
          <Grid item>
            <LabeledTextField
              type="number"
              value={kizuzHoni}
              textFieldWidth={185}
              name="kizuzHoni"
              label={HAKLAI_ANOTHER_QUESTIONS_LABELS.capitalLossOffset}
              helpDialog={haklaiHelpTexts.kizuzHoni}
              onChange={handleOnChange}
            />
          </Grid>
        </Grid>
        <Grid item>
          <Divider className="haklai-another-questions-divider" light />
        </Grid>
        <Grid container item direction="row" spacing={10} alignItems="center">
          <Grid item>
            <FormControl component="fieldset">
              <Grid container item direction="row" spacing={1} alignItems="center">
                <Grid item>
                  <Typography variant="subtitle1" align="right">
                    {HAKLAI_ANOTHER_QUESTIONS_LABELS.expropriationCredit}
                  </Typography>
                </Grid>
                <Grid item>
                  <HelpDialog
                    helpDialog={haklaiHelpTexts.afkaa}
                    label={HAKLAI_ANOTHER_QUESTIONS_LABELS.expropriationCredit}
                  />
                </Grid>
                <Grid item>
                  <RadioGroup
                    row
                    name="afkaa"
                    value={Boolean(afkaa).toString()}
                    onChange={handleOnChange}
                  >
                    <FormControlLabel
                      value={"true"}
                      control={<Radio color="primary" />}
                      label={HAKLAI_ANOTHER_QUESTIONS_LABELS.yes}
                      labelPlacement="end"
                    />
                    <FormControlLabel
                      value={"false"}
                      control={<Radio color="primary" />}
                      label={HAKLAI_ANOTHER_QUESTIONS_LABELS.no}
                      labelPlacement="end"
                    />
                  </RadioGroup>
                </Grid>
              </Grid>
              <Typography variant="caption" color="error" style={FIELD_ERROR_STYLE}>
                {" "}
              </Typography>
            </FormControl>
          </Grid>
        </Grid>
        </Grid>
      </Box>
    </HaklaiSectionAccordion>
  );
};

export default React.memo(HaklaiAnotherQuestionsSection);
